import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import { toast } from "sonner";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { useState, useContext, useMemo, useEffect, useRef } from "react";
import { Upload, X, Plus } from "lucide-react";
import { trpc } from "../trpc";
import MerchantContext from "../contexts/MerchantContext";
import posthog from "../lib/posthog";

const MAX_FILE_SIZE_MB = 5;
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"] as const;
const ACCEPTED_VIDEO_TYPES = ["video/mp4", "video/3gpp"] as const;
const ACCEPTED_MEDIA_TYPES = [...ACCEPTED_IMAGE_TYPES, ...ACCEPTED_VIDEO_TYPES] as const;
type AcceptedMediaType = (typeof ACCEPTED_MEDIA_TYPES)[number];

type CreateTemplateModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

type TemplateCategory = "MARKETING" | "UTILITY" | "AUTHENTICATION";
type TemplateButton = { type: "QUICK_REPLY"; text: string };

const MAX_BUTTONS = 3;
const MAX_BUTTON_TEXT_LENGTH = 25;
const MIN_CODE_EXPIRATION_MINUTES = 1;
const MAX_CODE_EXPIRATION_MINUTES = 90;
const DEFAULT_OTP_BUTTON_TEXT = "Copiar código";

const CreateTemplateModal = ({
  isOpen,
  onClose,
  onSuccess,
}: CreateTemplateModalProps): JSX.Element => {
  const { merchantId } = useContext(MerchantContext);
  const isMobile = useIsMobile();

  const [whatsappConnectionId, setWhatsappConnectionId] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [category, setCategory] = useState<TemplateCategory>("MARKETING");
  const [language, setLanguage] = useState("es");
  const [bodyText, setBodyText] = useState("");
  const [paramExamples, setParamExamples] = useState<Record<string, string>>({});
  const [paramDefaults, setParamDefaults] = useState<Record<string, string>>({});
  const [headerMediaUrl, setHeaderMediaUrl] = useState<string | null>(null);
  const [headerMediaFormat, setHeaderMediaFormat] = useState<"image" | "video" | null>(null);
  const [isUploadingMedia, setIsUploadingMedia] = useState(false);
  const [buttons, setButtons] = useState<TemplateButton[]>([]);
  const [newButtonText, setNewButtonText] = useState("");
  const [securityRecommendation, setSecurityRecommendation] = useState(true);
  const [codeExpirationMinutes, setCodeExpirationMinutes] = useState("");
  const [otpButtonText, setOtpButtonText] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isAuth = category === "AUTHENTICATION";
  const parsedCodeExpirationMinutes = codeExpirationMinutes
    ? Number(codeExpirationMinutes)
    : undefined;
  const isCodeExpirationValid =
    parsedCodeExpirationMinutes === undefined ||
    (Number.isInteger(parsedCodeExpirationMinutes) &&
      parsedCodeExpirationMinutes >= MIN_CODE_EXPIRATION_MINUTES &&
      parsedCodeExpirationMinutes <= MAX_CODE_EXPIRATION_MINUTES);

  const { data: connections, isLoading: connectionsLoading } =
    trpc.whatsappConnections.list.useQuery({ merchantId }, { enabled: !!merchantId && isOpen });

  // Meta owns the authentication copy — the backend catalog renders what the merchant will see.
  const { data: authBodyPreview } = trpc.whatsappTemplates.previewAuthBody.useQuery(
    {
      language,
      securityRecommendation,
      codeExpirationMinutes: isCodeExpirationValid ? parsedCodeExpirationMinutes : undefined,
    },
    { enabled: isOpen && isAuth }
  );

  const utils = trpc.useUtils();

  const uploadTemplateMedia = trpc.whatsappTemplates.uploadTemplateMedia.useMutation({
    onError: (error) => {
      toast.error("Error al subir archivo", {
        description: error.message || "No se pudo subir el archivo",
      });
      setIsUploadingMedia(false);
    },
  });

  const createTemplate = trpc.whatsappTemplates.create.useMutation({
    onSuccess: async () => {
      posthog.capture("template created", {
        merchant_id: merchantId,
        template_name: templateName,
        category,
        language,
        has_media: !!headerMediaUrl,
        button_count: buttons.length,
      });
      await utils.whatsappTemplates.list.invalidate({ merchantId });
      toast.success("Plantilla creada", {
        description: "La plantilla se ha creado correctamente",
      });
      handleClose();
      onSuccess();
    },
    onError: async (error) => {
      // A failed attempt is persisted as a FAILED template row — refresh the list
      // so it appears with its reason without a manual reload.
      await utils.whatsappTemplates.list.invalidate({ merchantId });
      toast.error("Error", {
        description: error.message || "No se pudo crear la plantilla",
      });
    },
  });

  // Extract parameters from body text
  const detectedParams = useMemo(() => {
    const matches = bodyText.matchAll(/{{(\w+)}}/g);
    return Array.from(matches).map((match) => match[1]);
  }, [bodyText]);

  // Update paramExamples when detected params change
  useEffect(() => {
    const newExamples: Record<string, string> = {};
    detectedParams.forEach((param) => {
      newExamples[param] = paramExamples[param] || "";
    });
    setParamExamples(newExamples);
  }, [detectedParams]);

  // Keep paramDefaults in sync with the detected params (backstop values the renderer
  // uses when a send omits a param — the backend now requires one per placeholder).
  useEffect(() => {
    const newDefaults: Record<string, string> = {};
    detectedParams.forEach((param) => {
      newDefaults[param] = paramDefaults[param] || "";
    });
    setParamDefaults(newDefaults);
  }, [detectedParams]);

  const handleClose = () => {
    setWhatsappConnectionId("");
    setTemplateName("");
    setCategory("MARKETING");
    setLanguage("es");
    setBodyText("");
    setParamExamples({});
    setParamDefaults({});
    setHeaderMediaUrl(null);
    setHeaderMediaFormat(null);
    setIsUploadingMedia(false);
    setButtons([]);
    setNewButtonText("");
    setSecurityRecommendation(true);
    setCodeExpirationMinutes("");
    setOtpButtonText("");
    onClose();
  };

  const handleAddButton = () => {
    const trimmedText = newButtonText.trim();
    if (!trimmedText) return;

    if (trimmedText.length > MAX_BUTTON_TEXT_LENGTH) {
      toast.warning("Texto demasiado largo", {
        description: `El texto del botón no puede exceder ${MAX_BUTTON_TEXT_LENGTH} caracteres`,
      });
      return;
    }

    if (buttons.length >= MAX_BUTTONS) {
      toast.warning("Límite alcanzado", {
        description: `Solo puedes agregar hasta ${MAX_BUTTONS} botones`,
      });
      return;
    }

    setButtons([...buttons, { type: "QUICK_REPLY", text: trimmedText }]);
    setNewButtonText("");
  };

  const handleRemoveButton = (index: number) => {
    setButtons(buttons.filter((_, i) => i !== index));
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ACCEPTED_MEDIA_TYPES.includes(file.type as AcceptedMediaType)) {
      toast.error("Tipo de archivo no válido", {
        description: "Solo se permiten imágenes (JPG, PNG, WebP) o videos (MP4, 3GPP)",
      });
      return;
    }

    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > MAX_FILE_SIZE_MB) {
      toast.error("Archivo demasiado grande", {
        description: `El archivo debe ser menor a ${MAX_FILE_SIZE_MB}MB`,
      });
      return;
    }

    const format = ACCEPTED_IMAGE_TYPES.includes(file.type as (typeof ACCEPTED_IMAGE_TYPES)[number])
      ? "image"
      : "video";
    setIsUploadingMedia(true);

    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = (reader.result as string).split(",")[1];

      try {
        const result = await uploadTemplateMedia.mutateAsync({
          merchantId,
          filename: file.name,
          contentType: file.type as AcceptedMediaType,
          base64Data: base64,
        });
        setHeaderMediaUrl(result.url);
        setHeaderMediaFormat(format);
        toast.success(format === "video" ? "Video subido" : "Imagen subida", {
          description: "El archivo se ha subido correctamente",
        });
      } finally {
        setIsUploadingMedia(false);
      }
    };
    reader.readAsDataURL(file);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveMedia = () => {
    setHeaderMediaUrl(null);
    setHeaderMediaFormat(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!merchantId || !whatsappConnectionId) return;

    if (isAuth) {
      if (!isCodeExpirationValid) {
        toast.error("Caducidad inválida", {
          description: `El código debe caducar entre ${MIN_CODE_EXPIRATION_MINUTES} y ${MAX_CODE_EXPIRATION_MINUTES} minutos`,
        });
        return;
      }

      await createTemplate.mutateAsync({
        merchantId,
        whatsappConnectionId,
        config: {
          name: templateName,
          category: "AUTHENTICATION",
          language,
          securityRecommendation,
          ...(parsedCodeExpirationMinutes === undefined
            ? {}
            : { codeExpirationMinutes: parsedCodeExpirationMinutes }),
          ...(otpButtonText.trim() ? { otpButtonText: otpButtonText.trim() } : {}),
        },
        params: [],
      });
      return;
    }

    // Validate variables are not at the start or end of the template
    // Must have at least one alphanumeric character before first variable and after last variable
    const trimmedBody = bodyText.trim();
    const firstVarIndex = trimmedBody.indexOf("{{");
    const lastVarIndex = trimmedBody.lastIndexOf("}}");

    if (firstVarIndex !== -1) {
      const beforeFirstVar = trimmedBody.substring(0, firstVarIndex);
      if (!/\w/.test(beforeFirstVar)) {
        toast.error("Error de formato", {
          description: "Debe haber texto antes de la primera variable (la puntuación no cuenta)",
        });
        return;
      }
    }

    if (lastVarIndex !== -1) {
      const afterLastVar = trimmedBody.substring(lastVarIndex + 2);
      if (!/\w/.test(afterLastVar)) {
        toast.error("Error de formato", {
          description: "Debe haber texto después de la última variable (la puntuación no cuenta)",
        });
        return;
      }
    }

    // Validate param names are lowercase
    const invalidParams = detectedParams.filter((param) => !/^[a-z_]+$/.test(param));
    if (invalidParams.length > 0) {
      toast.error("Nombres de parámetros inválidos", {
        description: `Los nombres de parámetros solo pueden contener letras minúsculas y guiones bajos: ${invalidParams.join(", ")}`,
      });
      return;
    }

    // Validate all params have examples
    const missingExamples = detectedParams.filter((param) => !paramExamples[param]?.trim());
    if (missingExamples.length > 0) {
      toast.warning("Ejemplos faltantes", {
        description: `Por favor proporciona ejemplos para: ${missingExamples.join(", ")}`,
      });
      return;
    }

    // Validate all params have a runtime default (the renderer's backstop — enforced by the backend too)
    const missingDefaults = detectedParams.filter((param) => !paramDefaults[param]?.trim());
    if (missingDefaults.length > 0) {
      toast.warning("Valores por defecto faltantes", {
        description: `Por favor proporciona un valor por defecto para: ${missingDefaults.join(", ")}`,
      });
      return;
    }

    const params = detectedParams.map((param) => ({
      name: param,
      example: paramExamples[param],
    }));

    await createTemplate.mutateAsync({
      merchantId,
      whatsappConnectionId,
      config: {
        name: templateName,
        category,
        ...(detectedParams.length > 0 ? { parameter_format: "NAMED" as const } : {}),
        language,
        header:
          headerMediaUrl && headerMediaFormat
            ? {
                format: headerMediaFormat,
                link: headerMediaUrl,
              }
            : undefined,
        body: {
          text: bodyText,
          ...(detectedParams.length > 0
            ? {
                defaults: Object.fromEntries(
                  detectedParams.map((param) => [param, paramDefaults[param].trim()])
                ),
              }
            : {}),
        },
        buttons: buttons.length > 0 ? buttons : undefined,
      },
      params,
    });
  };

  // Group connections by wabaId
  const connectionsByWaba = useMemo(() => {
    if (!connections) return [];

    const grouped = connections.reduce((acc, conn) => {
      const wabaId = conn.wabaId || "unknown";
      if (!acc[wabaId]) {
        acc[wabaId] = [];
      }
      acc[wabaId].push(conn);
      return acc;
    }, {} as Record<string, typeof connections>);

    return Object.entries(grouped);
  }, [connections]);

  const previewText = useMemo(() => {
    if (isAuth) return authBodyPreview?.text || "";
    if (!bodyText) return "";

    return bodyText.replace(/{{(\w+)}}/g, (match, param) => {
      const example = paramExamples[param];
      return example ? example : match;
    });
  }, [isAuth, authBodyPreview, bodyText, paramExamples]);

  const previewButtonLabels = isAuth
    ? [otpButtonText.trim() || DEFAULT_OTP_BUTTON_TEXT]
    : buttons.map((button) => button.text);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="h-full max-h-full w-full md:h-auto md:max-h-[85vh] md:max-w-6xl md:max-w-[90vw]">
        <DialogHeader>
          <DialogTitle>Crear plantilla de WhatsApp</DialogTitle>
        </DialogHeader>
        <div className="pb-6">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-[1fr_1px_1fr]">
            {/* Form Section */}
            <div>
              <form onSubmit={handleSubmit}>
                <div className="flex flex-col gap-4">
                  <p className="text-sm text-gray-600">
                    Las plantillas deben ser aprobadas por WhatsApp antes de poder ser usadas.
                  </p>

                  <div className="flex flex-col gap-2">
                    <Label className="text-sm font-medium">
                      Conexión de WhatsApp <span className="text-red-500">*</span>
                    </Label>
                    <select
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                      value={whatsappConnectionId}
                      onChange={(e) => setWhatsappConnectionId(e.target.value)}
                      disabled={connectionsLoading}
                    >
                      <option value="">Selecciona una conexión</option>
                      {connectionsByWaba.map(([wabaId, conns]) => (
                        <optgroup key={wabaId} label={`WABA: ${wabaId}`}>
                          {conns.map((conn) => (
                            <option key={conn.id} value={conn.id}>
                              {conn.phoneNumber || conn.phoneNumberId}
                              {conn.isPrimary && " (Principal)"}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>

                  <div className="flex flex-col gap-2">
                    <Label className="text-sm font-medium">
                      Nombre de la plantilla <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      value={templateName}
                      onChange={(e) => {
                        // Convert to lowercase and remove invalid characters
                        const sanitized = e.target.value
                          .toLowerCase()
                          .replace(/[^a-z0-9_]/g, "");
                        // Ensure first character is a letter (remove leading numbers/underscores)
                        const withValidStart = sanitized.replace(/^[^a-z]+/, "");
                        setTemplateName(withValidStart);
                      }}
                      placeholder="nombre_de_la_plantilla"
                    />
                    <p className="mt-1 text-xs text-gray-600">
                      Solo letras minúsculas, números y guiones bajos. Debe comenzar con una letra.
                    </p>
                  </div>

                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <Label className="text-sm font-medium">
                        Categoría <span className="text-red-500">*</span>
                      </Label>
                      <a
                        href="https://developers.facebook.com/documentation/business-messaging/whatsapp/templates/template-categorization"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-500"
                      >
                        (¿Qué categoría elegir?)
                      </a>
                    </div>
                    <select
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                      value={category}
                      onChange={(e) => setCategory(e.target.value as TemplateCategory)}
                    >
                      <option value="MARKETING">Marketing</option>
                      <option value="UTILITY">Utilidad</option>
                      <option value="AUTHENTICATION">Autenticación</option>
                    </select>
                  </div>

                  <div className="flex flex-col gap-2">
                    <Label className="text-sm font-medium">
                      Idioma <span className="text-red-500">*</span>
                    </Label>
                    <select
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                      value={language}
                      onChange={(e) => setLanguage(e.target.value)}
                    >
                      <option value="es">Español</option>
                      <option value="en">Inglés</option>
                      <option value="pt">Portugués</option>
                    </select>
                  </div>

                  {isAuth && (
                    <>
                      <div className="rounded-md border bg-gray-50 p-4">
                        <p className="mb-2 text-sm font-bold">Cuerpo del mensaje</p>
                        <p className="mb-3 text-xs text-gray-600">
                          WhatsApp define y traduce el texto de las plantillas de autenticación. No
                          es editable.
                        </p>
                        <p className="whitespace-pre-wrap text-sm text-gray-800">
                          {authBodyPreview?.text || "Cargando..."}
                        </p>
                      </div>

                      <label className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          className="mt-1 size-4"
                          checked={securityRecommendation}
                          onChange={(e) => setSecurityRecommendation(e.target.checked)}
                        />
                        <span>
                          <span className="text-sm font-medium">
                            Incluir recomendación de seguridad
                          </span>
                          <span className="mt-1 block text-xs text-gray-600">
                            Agrega la frase que pide al destinatario no compartir el código.
                          </span>
                        </span>
                      </label>

                      <div className="flex flex-col gap-2">
                        <Label className="text-sm font-medium">
                          Caducidad del código en minutos (opcional)
                        </Label>
                        <Input
                          type="number"
                          min={MIN_CODE_EXPIRATION_MINUTES}
                          max={MAX_CODE_EXPIRATION_MINUTES}
                          value={codeExpirationMinutes}
                          onChange={(e) => setCodeExpirationMinutes(e.target.value)}
                          placeholder="10"
                        />
                        <p className="mt-1 text-xs text-gray-600">
                          Entre {MIN_CODE_EXPIRATION_MINUTES} y {MAX_CODE_EXPIRATION_MINUTES}{" "}
                          minutos. Se muestra como pie de página del mensaje.
                        </p>
                      </div>

                      <div className="flex flex-col gap-2">
                        <Label className="text-sm font-medium">
                          Texto del botón de copiado (opcional)
                        </Label>
                        <Input
                          value={otpButtonText}
                          onChange={(e) => setOtpButtonText(e.target.value)}
                          placeholder={DEFAULT_OTP_BUTTON_TEXT}
                          maxLength={MAX_BUTTON_TEXT_LENGTH}
                        />
                        <p className="mt-1 text-xs text-gray-600">
                          Máximo {MAX_BUTTON_TEXT_LENGTH} caracteres. Si lo dejas vacío, WhatsApp usa
                          su texto por defecto.
                        </p>
                      </div>
                    </>
                  )}

                  {!isAuth && (
                    <>
                  <div className="flex flex-col gap-2">
                    <Label className="text-sm font-medium">Imagen o video de encabezado (opcional)</Label>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      accept={ACCEPTED_MEDIA_TYPES.join(",")}
                      style={{ display: "none" }}
                    />
                    {headerMediaUrl ? (
                      <div className="rounded-md border bg-gray-50 p-3">
                        <div className="flex items-center gap-3">
                          {headerMediaFormat === "video" ? (
                            <video
                              src={headerMediaUrl}
                              className="max-h-[60px] max-w-[80px] rounded-sm object-contain"
                              muted
                              playsInline
                            />
                          ) : (
                            <img
                              src={headerMediaUrl}
                              alt="Header preview"
                              className="max-h-[60px] rounded-sm object-contain"
                            />
                          )}
                          <span className="flex-1 truncate text-sm text-gray-600">
                            {headerMediaFormat === "video" ? "Video subido" : "Imagen subida"}
                          </span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-500 hover:bg-red-50 hover:text-red-700"
                            aria-label="Eliminar archivo"
                            onClick={handleRemoveMedia}
                          >
                            <X className="size-4" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Button
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isUploadingMedia}
                        className="w-full"
                      >
                        {isUploadingMedia ? (
                          <Spinner className="mr-2 size-4" />
                        ) : (
                          <Upload className="mr-2 size-4" />
                        )}
                        {isUploadingMedia ? "Subiendo..." : "Subir imagen o video"}
                      </Button>
                    )}
                    <p className="mt-1 text-xs text-gray-600">
                      JPG, PNG, WebP, MP4 o 3GPP. Máximo {MAX_FILE_SIZE_MB}MB. Video solo para Marketing
                      o Utilidad.
                    </p>
                  </div>

                  <div className="flex flex-col gap-2">
                    <Label className="text-sm font-medium">
                      Cuerpo del mensaje <span className="text-red-500">*</span>
                    </Label>
                    <Textarea
                      value={bodyText}
                      onChange={(e) => setBodyText(e.target.value)}
                      placeholder="Hola {{nombre}}, te escribimos para..."
                      rows={6}
                    />
                    <p className="mt-1 text-xs text-gray-600">
                      Usa {`{{nombre_parametro}}`} para agregar variables. Debe haber texto antes y
                      después de las variables.
                    </p>
                  </div>

                  {detectedParams.length > 0 && (
                    <div className="rounded-md border bg-gray-50 p-4">
                      <p className="mb-3 text-sm font-bold">
                        Ejemplos de parámetros
                      </p>
                      <div className="flex flex-col gap-3">
                        {detectedParams.map((param) => {
                          const isInvalidParam = !/^[a-z_]+$/.test(param);
                          return (
                          <div key={param} className="flex flex-col gap-2">
                            <Label className="text-sm">
                              Ejemplo para {`{{${param}}}`} <span className="text-red-500">*</span>
                            </Label>
                            {isInvalidParam && (
                              <p className="text-xs text-red-500">
                                El nombre del parámetro solo puede contener letras minúsculas y guiones bajos
                              </p>
                            )}
                            <Input
                              value={paramExamples[param] || ""}
                              onChange={(e) =>
                                setParamExamples((prev) => ({
                                  ...prev,
                                  [param]: e.target.value,
                                }))
                              }
                              placeholder={`Ejemplo de ${param}`}
                              className="h-8 bg-white text-sm"
                            />
                          </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {detectedParams.length > 0 && (
                    <div className="rounded-md border bg-gray-50 p-4">
                      <p className="mb-1 text-sm font-bold">Valores por defecto</p>
                      <p className="mb-3 text-xs text-gray-600">
                        Se usan cuando no se envía un valor para la variable, para que el mensaje
                        nunca quede incompleto.
                      </p>
                      <div className="flex flex-col gap-3">
                        {detectedParams.map((param) => (
                          <div key={param} className="flex flex-col gap-2">
                            <Label className="text-sm">
                              Valor por defecto para {`{{${param}}}`}{" "}
                              <span className="text-red-500">*</span>
                            </Label>
                            <Input
                              value={paramDefaults[param] || ""}
                              onChange={(e) =>
                                setParamDefaults((prev) => ({
                                  ...prev,
                                  [param]: e.target.value,
                                }))
                              }
                              placeholder={`Valor por defecto de ${param}`}
                              className="h-8 bg-white text-sm"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex flex-col gap-2">
                    <Label className="text-sm font-medium">Botones de respuesta rápida (opcional)</Label>
                    <div className="flex flex-col gap-2">
                      {buttons.map((button, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <div className="flex-1 rounded-md border bg-gray-50 p-2 text-sm">
                            {button.text}
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-500 hover:bg-red-50 hover:text-red-700"
                            aria-label="Eliminar botón"
                            onClick={() => handleRemoveButton(index)}
                          >
                            <X className="size-4" />
                          </Button>
                        </div>
                      ))}
                      {buttons.length < MAX_BUTTONS && (
                        <div className="flex items-center gap-2">
                          <Input
                            value={newButtonText}
                            onChange={(e) => setNewButtonText(e.target.value)}
                            placeholder="Texto del botón"
                            className="h-8 text-sm"
                            maxLength={MAX_BUTTON_TEXT_LENGTH}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                handleAddButton();
                              }
                            }}
                          />
                          <Button
                            size="icon"
                            className="bg-green-600 text-white hover:bg-green-700"
                            onClick={handleAddButton}
                            disabled={!newButtonText.trim()}
                          >
                            <Plus className="size-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-gray-600">
                      Máximo {MAX_BUTTONS} botones, {MAX_BUTTON_TEXT_LENGTH} caracteres cada uno
                    </p>
                  </div>
                    </>
                  )}

                  <div className="flex items-center gap-3 pt-2">
                    <Button variant="ghost" onClick={handleClose} className="w-full">
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      className="w-full bg-green-600 text-white hover:bg-green-700"
                      disabled={
                        createTemplate.isLoading ||
                        !whatsappConnectionId ||
                        !templateName ||
                        (!isAuth && !bodyText)
                      }
                    >
                      {createTemplate.isLoading ? "Creando..." : "Crear plantilla"}
                    </Button>
                  </div>
                </div>
              </form>
            </div>

            {/* Divider -- hidden on mobile */}
            <Separator orientation="vertical" className="hidden md:block" />

            {/* Toggle preview button -- mobile only */}
            {isMobile && (
              <Button
                variant="outline"
                onClick={() => setShowPreview(!showPreview)}
                className="min-h-11 w-full"
              >
                {showPreview ? "Ocultar vista previa" : "Ver vista previa"}
              </Button>
            )}

            {/* Preview Section -- always on desktop, togglable on mobile */}
            <div className={isMobile && !showPreview ? "hidden" : "block"}>
              <div className="flex h-full flex-col gap-4">
                <div>
                  <p className="mb-2 text-lg font-bold">
                    Vista Previa
                  </p>
                  <p className="text-sm text-gray-600">
                    Así se verá tu mensaje con los ejemplos que proporciones
                  </p>
                </div>

                {/* WhatsApp Message Preview */}
                <div className="flex min-h-[200px] flex-1 items-start justify-center rounded-md bg-gray-50 p-4 md:min-h-[400px]">
                  {previewText || headerMediaUrl ? (
                    <div className="w-full md:w-[320px]">
                      <div className="relative overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm before:absolute before:left-[-8px] before:top-0 before:h-0 before:w-0 before:border-[0_8px_8px_0] before:border-solid before:border-[transparent_white_transparent_transparent] before:content-['']">
                        {!isAuth &&
                          headerMediaUrl &&
                          (headerMediaFormat === "video" ? (
                            <video
                              src={headerMediaUrl}
                              className="max-h-[200px] w-full object-cover"
                              muted
                              playsInline
                            />
                          ) : (
                            <img
                              src={headerMediaUrl}
                              alt="Header"
                              className="max-h-[200px] w-full object-cover"
                            />
                          ))}
                        {previewText && (
                          <div className="p-4">
                            <p className="whitespace-pre-wrap text-sm text-gray-800">
                              {previewText}
                            </p>
                          </div>
                        )}
                        {previewButtonLabels.length > 0 && (
                          <div className="flex w-full flex-col">
                            {previewButtonLabels.map((label, index) => (
                              <div
                                key={index}
                                className="w-full border-t border-gray-200 py-3 text-center text-sm font-medium text-blue-500"
                              >
                                {label}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center text-gray-400">
                      <p className="text-sm">
                        {isAuth
                          ? "Cargando la vista previa..."
                          : "Escribe el cuerpo del mensaje para ver la vista previa"}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateTemplateModal;
